/**
 * I show how Vault works through bubble walkthroughs on the real UI.
 * Each story is a sequence of steps: navigate to a path, spotlight the target
 * element (data-guide anchors), show a bubble with the story text, and either
 * advance on Next or wait for the user to actually CLICK the highlighted thing
 * (advanceOn: 'click'), following URL changes as they happen. Deep-links work:
 * steps navigate for you. No AI needed here — a clear story beats a chatbot.
 *
 * Author: Maré Greyling (Thevertex) <mare.greyling@gmail.com> — https://mare-greyling.com
 */
import { useCallback, useEffect, useRef, useState } from 'react';
import { taxPackAvailable } from '../global.js';
import { useLocation, useNavigate } from 'react-router-dom';
import { api } from '../api.js';
import SearchField from './SearchField.jsx';
import { useViewMode } from './simple/viewMode.jsx';

// ── Guide voice ───────────────────────────────────────────────────────────
// 'device' = the browser's own speech (free, offline, instant);
// 'ai' = Gemini's natural voice via /budget/guides/speak/ (cached per text,
// cost logged as guide_voice). Any AI failure falls back to the device voice.
let guideAudio = null;
let speechRequest = 0;
/** Cancel active speech and invalidate pending responses from earlier steps. */
export function stopSpeaking() {
  speechRequest += 1;
  try { window.speechSynthesis?.cancel(); } catch { /* no speech */ }
  if (guideAudio) { try { guideAudio.pause(); } catch { /* ok */ } guideAudio = null; }
}
function deviceSpeak(text) {
  try {
    const ss = window.speechSynthesis;
    if (!ss) return false;
    const u = new SpeechSynthesisUtterance(text);
    const vs = ss.getVoices();
    const v = vs.find((x) => /en-ZA/i.test(x.lang)) || vs.find((x) => /en-GB/i.test(x.lang))
      || vs.find((x) => /^en/i.test(x.lang));
    if (v) u.voice = v;
    ss.speak(u);
    return true;
  } catch { return false; }
}
export async function speakText(text, engine) {
  stopSpeaking();
  const request = speechRequest;
  if (!text) return;
  if (engine === 'ai') {
    try {
      const d = await api('/budget/guides/speak/', { method: 'POST', body: { text } });
      if (request !== speechRequest) return;
      if (d?.audio) {
        guideAudio = new Audio(`data:audio/wav;base64,${d.audio}`);
        await guideAudio.play();
        return;
      }
    } catch { /* fall back to the device voice */ }
    if (request !== speechRequest) return;
  }
  deviceSpeak(text);
}

// The tax tour is SARS-only, so it is offered to South African accounts only.
const visibleTours = () => TOURS.filter((t) => t.id !== 'tax' || taxPackAvailable());

const TOURS = [
  {
    id: 'welcome',
    title: 'Welcome to Vault',
    blurb: 'A 60-second tour of the essentials.',
    steps: [
      { path: '/app', target: '[data-guide="leftover"]',
        title: 'This number is the point of Vault',
        body: 'Your REAL leftover \u2014 what is in your banks minus what they still owe. Everything you log keeps it honest.' },
      { path: '/app', target: '[data-guide="bank-cards"]',
        title: 'Each bank keeps its own truth',
        body: 'Every bank card shows what is in it, what it owes and its real leftover \u2014 that per-bank honesty is what makes the headline number real.' },
      { path: '/app', target: '[data-guide="open-map"]',
        title: 'Log where you actually are',
        body: 'The Spend Map logs by place, and Vault nudges you when you arrive at a saved shop \u2014 on your phone and your watch.' },
      { path: '/app', target: '[data-guide="add-expense"]',
        title: 'One + for everything',
        body: 'This button logs spending, income AND savings \u2014 all in one sheet. Press Next and I will open it.' },
      { path: '/app', clickFirst: '[data-guide="add-expense"]', activate: '[data-guide="add-kind-expense"]', target: '[data-guide="add-title"]', fill: 'Coffee',
        title: 'Name it \u2014 one word is enough',
        body: 'I typed a sample name. Watch what AI does with it next.' },
      { path: '/app', clickFirst: '[data-guide="add-expense"]', activate: '[data-guide="add-ai-run"]', target: '[data-guide="add-note"]',
        title: 'Watch the AI work',
        body: 'From just "Coffee" Vault writes the note, picks a category from YOUR tabs and guesses the bank \u2014 live, and all yours to change.' },
      { path: '/app', clickFirst: '[data-guide="add-expense"]', target: '[data-guide="add-scan"]',
        title: 'Or scan a receipt',
        body: 'Tap the Scan button and AI reads a till slip (a spend) or a quote (a planned expense) \u2014 shop, amount, date and category.' },
      { path: '/app', clickFirst: '[data-guide="add-expense"]', target: '[data-guide="add-save"]',
        title: 'Save \u2014 and that is the whole idea',
        body: 'Save lands it in this month. Log honestly, let AI do the typing, and your leftover stays true \u2014 every other walkthrough lives behind the \ud83d\udcd6 button, any time.' },
    ],
  },
  {
    id: 'multi-account',
    title: 'Two accounts at one bank',
    blurb: 'Name each account so a second one at the same bank stays clear.',
    steps: [
      { path: '/app', target: '[data-guide="bank-cards"]',
        title: 'Each account is its own card',
        body: 'Two accounts at the same bank each get their own card, balance and real leftover \u2014 Vault never mixes them. Every line, and each statement import, stays tied to the account you pick.' },
      { path: '/manage', target: '[data-guide="bank-add-account"]',
        title: 'Add the same bank twice',
        body: 'Type the bank, then give this account a short name (e.g. Salary, Savings). Add the bank again with a different name to track a second account there.' },
    ],
  },
  {
    id: 'first-expense',
    title: 'Log an expense or income',
    blurb: 'Type one word — AI fills the rest.',
    steps: [
      { path: '/app', target: '[data-guide="leftover"]',
        title: 'This number is the point of Vault',
        body: 'Your REAL leftover — what is in your banks minus what they still owe. Everything you log keeps it honest.' },
      { path: '/app', target: '[data-guide="add-expense"]',
        title: 'One + for everything',
        body: 'This button logs spending, income AND savings — all in one sheet. Press Next and I will open it.' },
      { path: '/app', clickFirst: '[data-guide="add-expense"]', target: '[data-guide="add-kind"]',
        title: 'All three live here: Expense · Income · Savings',
        body: 'These are ALL the ways to log — Expense, Income and 🐷 Savings. That is the whole set, and I will show each one.' },
      { path: '/app', clickFirst: '[data-guide="add-expense"]', activate: '[data-guide="add-kind-expense"]', target: '[data-guide="add-title"]', fill: 'Coffee',
        title: 'Name it — one word is enough',
        body: 'I typed a sample name. Watch what AI does with it next.' },
      { path: '/app', clickFirst: '[data-guide="add-expense"]', activate: '[data-guide="add-ai-run"]', target: '[data-guide="add-note"]',
        title: 'Watch the AI work, right now',
        body: 'I only typed "Coffee" and nothing else. Watch this field: in a second Vault reads that one word and writes the note, picks a category from YOUR own tabs, and guesses the likely bank from your history. Real, live, and every field stays yours to change.' },
      { path: '/app', clickFirst: '[data-guide="add-expense"]', target: '[data-guide="add-scan"]',
        title: 'This is where AI does the work',
        body: 'Type one word and AI fills the category, a note and the likely bank from your history — all overridable. Or tap 📷 Scan: AI reads a till slip (a spend) or a quote (a planned expense) — shop, amount, date and category.' },
      { path: '/app', clickFirst: '[data-guide="add-expense"]', target: '[data-guide="add-merge"]',
        title: 'Already have it? Merge, don\u2019t duplicate',
        body: 'If the name you type matches an expense you already track, Vault offers to merge this into it and set it active \u2014 so you never end up with two \u201CGroceries\u201D. One tap keeps the budget clean.' },
      { path: '/app', clickFirst: '[data-guide="add-expense"]', target: '[data-guide="add-amount"]', fill: '200*31',
        title: 'A plain amount — or a FORMULA',
        body: 'I put 200×31: a formula pays off day by day with a − / + stepper. Or just type 450 for a flat amount.' },
      { path: '/app', clickFirst: '[data-guide="add-expense"]', target: '[data-guide="add-bank"]',
        title: 'Which bank — AI guesses',
        body: 'The AI already filled the likely bank the moment you named it. See it selected? Leave that guess or pick another in this bank box. Logging a spend on an existing expense works the same way: in Simple view tap Change under Which bank; on a row\'s card use Paid from. The money comes off the bank you pick, and only that bank. In Simple view, Log a spend also fills Where with the shop you are at (or the shop on your receipt): tap Change to pick a nearby shop, search one, tap Find shops near me, or choose No shop. The expense you use there comes first next time, and + New expense under What it was adds a new line with the spend on it.' },
      { path: '/app', clickFirst: '[data-guide="add-expense"]', target: '[data-guide="add-note"]',
        title: 'A note — AI writes it',
        body: 'See the note? The AI wrote it from "Coffee" a moment ago. Edit it freely or leave it. The category was AI-picked too, from your own tabs.' },
      { path: '/app', clickFirst: '[data-guide="add-expense"]', target: '[data-guide="add-toggles"]',
        title: 'Already paid? Every month?',
        body: 'Mark it already paid (or received for income), and flip Recurring on to seed it into every future month automatically.' },
      { path: '/app', clickFirst: '[data-guide="add-expense"]', target: '[data-guide="add-save"]',
        title: 'Save — that is the expense flow',
        body: 'Tap Save and it lands in this month. Now the other two kinds.' },
      { path: '/app', clickFirst: '[data-guide="add-expense"]', activate: '[data-guide="add-kind-income"]', target: '[data-guide="add-amount"]', fill: '18000',
        title: 'Income — same flow, plus salary',
        body: 'Now on Income. Name it and set the amount just the same — and if you flag it as your SALARY, ticking it received is what starts your new month.' },
      { path: '/app', clickFirst: '[data-guide="add-expense"]', activate: '[data-guide="add-kind-savings"]', target: '[data-guide="add-savings-amount"]', fill: '500',
        title: '🐷 Savings — deposit or withdraw',
        body: 'And Savings: just an amount into or out of your pot, with the bank it moves from. That is all three kinds, start to finish.' },
        ],
  },
  {
    id: 'daily-rows',
    title: 'Your day-to-day: rows and the detail card',
    blurb: 'Tick to pay, click to edit — everything lives one tap deep.',
    steps: [
      { path: '/app', target: '[data-guide="leftover"]',
        title: 'The rows feed this number',
        body: 'Every expense and income line below keeps your real leftover honest. A row SHOWS at a glance: the coloured ring (emerald = received, blue = paid), the bank, category, formula and spent-vs-left. The totals strip above reads the current view.' },
      { path: '/app', target: '[data-guide="month-search"]',
        title: 'Find anything as you type',
        body: 'This box filters the whole month live - title, category, bank, note - and stacks with the bank chips. Manage > Recurring has the same live search.' },
      { path: '/app', target: '[data-guide="expense-row"]',
        title: 'Every line is one tap deep',
        body: 'Click any row to open its detail card: Record what you spent (the -/+ units stepper, Log spend, ✓ Just visited, 📷 Scan a receipt), then Change for the setup - amount or formula, category, Paid from, the place and the Tax-deductible (SARS) tick. The SARS tax bucket sits under More options. Tick the ring to mark it paid or received.' },
      { path: '/app', target: '[data-guide="monthly-check"]',
        title: 'Monthly Check watches the month',
        body: 'Its card shows the counts - how many notes, how many clear in one tap and how many need a look: where you saved, where you overspent, which bank is short. Tap Show them when you want the detail; the notes start hidden again on every visit.' },
      { path: '/app', target: '[data-guide="move-money"]',
        title: 'Move money, settle and delete - all one tap',
        body: 'Under-spent tick asks: settle at the tracked figure (the difference frees back to its bank) or paid in full. Delete asks how far it reaches. Transfers between your own banks are moves, not spending, and Vault records them that way.' },
      { path: '/app', target: '[data-guide="bank-cards"]',
        title: 'Each bank keeps its own truth',
        body: 'Every bank card shows what is in it, what it still owes, and its real leftover - tap one to expand. That per-bank honesty is what makes the headline leftover real.' },
        ],
  },
  {
    id: 'spend-map',
    title: 'The Spend Map',
    blurb: 'See where your money actually goes — on a real map.',
    steps: [
      { path: '/app', target: '[data-guide="map-search"]', open: 'clear',
        title: 'Find a shop — closest first',
        body: 'Type a shop or address. Real places list as you type, NEAREST to you first — "tennis" finds the closest courts, not a famous far one. AI (Gemini + Claude) finds the branch near you, not the head office, and reads it back before it saves. Nothing saves until you pick one.' },
      { path: '/app', target: null,
        title: 'Flag tax on the map too',
        body: 'An expense opened on the map uses the same detail card: tick Tax-deductible (SARS) under Change and pick the tax bucket under More options. A shop event\'s editor has its own Tax-deductible tick and SARS bucket too, so you can flag a deductible right where you spend. Your tracked trips become the SARS travel logbook automatically.' },
      { path: '/app', target: '[data-guide="map-pin-here"]', open: 'clear',
        title: '🏠 Pin where you stand',
        body: 'This drops a saved place at your EXACT spot — no searching your own address. Great for home, work, or anywhere you spend often.' },
      { path: '/app', target: '[data-guide="map-trip"]', open: 'clear',
        title: '🚗 Track a trip',
        body: 'Records your GPS route as you travel; at the end it asks what it cost and settles it against a transport expense — perfect for Ubers and petrol runs.' },
      { path: '/app', target: '[data-guide="map-trips"]', open: 'clear',
        title: '🧭 See the road you drove',
        body: 'After a trip, tap 🧭 Trips to list every journey with its distance and cost — a SARS badge marks business trips. Pick one and Vault traces the exact road you drove on the map.' },
      { path: '/app', target: '[data-guide="map-link-panel"]', open: 'link',
        title: 'Link a shop to an expense',
        body: 'This is the real linking panel — opened on one of your shops. It lists your expenses (formula ones show their R-per-unit). Pick the one that belongs here.' },
      { path: '/app', target: '[data-guide="map-link-panel"]', open: 'link',
        title: '🏠 Its home — the shop is its home',
        body: '🏠 Its home makes this shop the expense\'s constant HOME: recurring-linked, so future months place it themselves. You log the actual usage yourself, whenever it happens.' },
      { path: '/app', target: '[data-guide="map-repeat"]', open: 'link-advanced',
        title: '📅 Dated & recurring',
        body: '📅 Dated & recurring logs a dated spend: for a FORMULA expense you set units (the R-per-unit shows), otherwise a rand amount — on any date. The How often buttons below set how it repeats.' },
      { path: '/app', target: '[data-guide="map-repeat"]', open: 'link-advanced',
        title: 'How often: One-off · Daily · Weekly · Monthly',
        body: 'One-off, Daily (with Times a day), Weekly (Which days?) or Monthly (Which day of the month?). Or tick Only when I\'m there — nothing is scheduled; it logs by GPS when your phone detects you at the shop and you approve.' },
      { path: '/app', target: '[data-guide="map-daterange"]', open: 'clear',
        title: '🗓 Replay any day',
        body: 'Pick a date with 🗓 in the bar at the top and choose By place, By expense or All. Events due that day show as "expected", ready to confirm. Whole month takes you back to the full month.' },
      { path: '/app', target: '[data-guide="map-expense"]', open: 'expense',
        title: 'Open an expense',
        body: 'Clicking an expense opens its panel — confirm the day\'s visits, add a dated spend, or open its Setup. Every logged item is deletable and reverses fully.' },
      { path: '/app', target: '[data-guide="map-places"]', open: 'clear',
        title: 'This month by place — and the pins',
        body: 'Each place shows what was spent there and units done, plus leftover. Click a shop to rename, move or delete it, or unlink an expense (its spend stays). Click a PIN for its full breakdown.' },
      // Spend Map deltas (updated mockup): the Spent/Coming and by-place/by-expense views, the
      // two-stepper case, how a log is entered, the slip read, and that Guide +
      // Ask ride every screen — plus changing the AI's guess in place.
      { path: '/app', target: '[data-guide="map-daterange"]', open: 'clear',
        title: 'Spent or Coming, by place or by expense',
        body: 'For the whole month, Spent / Coming flips the list between money already spent and spend still to come, and This month by place sorts by Amount, Distance or Name - open a place to see its expenses. Pick a day with 🗓 and the chips By place, By expense and All regroup that day. Whole month brings the full view back. One row open at a time.' },
      { path: '/app', target: '[data-guide="map-expense"]', open: 'expense',
        title: 'Two steppers when an expense has two rates',
        body: 'An expense that is both a per-unit formula AND a standing event shows two labelled steppers — one for the R200/unit budget, one for the R12/day event — never one ambiguous +. The unit stepper only appears when there is a formula; a flat expense just takes an amount.' },
      { path: '/app', target: '[data-guide="map-expense"]', open: 'expense',
        title: 'Log an amount, a slip, or a visit',
        body: 'Every expense always has: type a rand amount, scan/upload a slip, or ✓ Just visited (R0, still counts). On an event you choose — tap + for one at the R12 rate, or scan the slip and Vault logs exactly what you spent; a slip can ride along with a + too.' },
      { path: '/app', target: '[data-guide="map-expense"]', open: 'expense',
        title: 'The slip does the typing',
        body: 'Scan on phone, upload on web. AI reads shop, amount, date, VAT and location, links it to this expense (or flags a better match) and drafts the logged line for you to confirm.' },
      { path: '/app', target: null, open: 'clear',
        title: 'Each screen has Guide + Ask',
        body: 'Every screen carries the 📖 guide button and the ? button (“Report a problem or request a change”), so help and change requests are one tap from wherever you are.' },
      { path: '/app', target: '[data-guide="map-expense"]', open: 'expense',
        title: 'Change the expense or the place, right here',
        body: 'AI’s guess is never locked in. On an expense’s card, Change opens its setup, where the place line has Move pin / change ▸. On a saved shop, Rename, Move pin and Merge sit right on the place — fix a mis-assigned spend or move a pin without a buried settings screen.' },
      { path: '/app', target: '[data-guide="map-events"]', open: 'clear',
        title: 'Shop events & near-shop nudges',
        body: 'Give a shop a recurring event and it shows on the map and Home with a − / + stepper. On mobile, Vault notices when you ARRIVE at a saved shop and offers to log; the watch shows nearby shops first. Tick the expense done and its event stops.' },
        ],
  },
  {
    id: 'cash-spend',
    title: 'Log a cash spend',
    steps: [
      { path: '/app', target: '[data-guide="cash-spend"]',
        title: '\ud83d\udcb5 Log a cash spend',
        body: 'This button sits beside \u21c4 Move money above your month. Once you have a money spot, the Cash (money spots) card up top has \ud83d\udcb5 Spend cash too \u2014 both open the same form. Cash is a money source like any account \u2014 spending it drops your cash on hand, reversibly.' },
      { path: '/app', clickFirst: '[data-guide="cash-spend"]', target: '[data-guide="cash-form"]',
        title: 'Cash is a source, like a bank',
        body: 'Spending physical cash? Log it here — it comes out of a money spot (Wallet, Safe), drops your cash on hand, and counts to its expense. Reversible, never a bank debit.' },
      { path: '/app', clickFirst: '[data-guide="cash-spend"]', target: '[data-guide="cash-form"]',
        title: 'Amount, spot, shop — with a read-back',
        body: 'Type the amount, pick the money spot it comes Out of, and optionally the Shop / location and the expense it is On. Vault reads it back in plain English before you tap ✓ Log cash spend — every part stays editable.' },
    ],
  },
  {
    id: 'transfers',
    title: 'Moving money between banks',
    blurb: 'Transfers are moves, not spending — Vault records them that way.',
    steps: [
      { path: '/app', target: '[data-guide="move-money"]',
        title: 'Move money yourself, any time',
        body: 'Tap \u21c4 Move money to record a transfer between your banks by hand — pick from, to and the amount. It logs as a MOVE, never as new income or a spend, so both banks update and your month totals stay honest.' },
      { path: '/app', target: '[data-guide="monthly-check"]',
        title: 'Monthly Check suggests the moves',
        body: 'When a bank runs short and another has spare, it proposes the exact transfer — one tap to approve, and both banks\' balances update.' },
      { path: '/app', target: null,
        title: 'Each bank keeps a note of it',
        body: 'Pick a bank chip above your month list and you\'ll see its transfer notes — “→ TymeBank: R 500,00” on the sender, “← Capitec: R 500,00” on the receiver. They never count as spending or income, so your month totals stay honest; the All-banks view skips them entirely because globally, nothing changed.' },
    ],
  },
  {
    id: 'manage-recurring',
    title: 'Manage your recurring setup',
    blurb: 'Rename, reprice, redo the AI — you choose which month it hits.',
    steps: [
      { path: '/app', target: '[data-guide="open-manage"]',
        title: 'Open Manage',
        body: 'Manage holds the machinery behind your month - banks, your recurring setup and savings. Press Next and I will open it.' },
      { path: '/manage', target: '[data-guide="manage-tabs"]',
        title: 'One place for the machinery',
        body: 'Banks, Recurring, Savings, Cash, Rules, Logged and Tax each get a tab. Nothing here touches a single month until you say so.' },
      { path: '/manage', target: '[data-guide="tab-logged"]', activate: '[data-guide="tab-logged"]',
        title: '📋 Everything you logged',
        body: 'The Logged tab is one place to see every spend, unit tick and settled trip for this month — each with its bank and amount, and deletable (it reverses fully).' },
      { path: '/manage', target: '[data-guide="tab-recurring"]', activate: '[data-guide="tab-recurring"]',
        title: 'Open Recurring',
        body: 'Tap Recurring to see every item that seeds into each new month.' },
      { path: '/manage', target: '[data-guide="recurring-name"]', activate: '[data-guide="tab-recurring"]',
        title: 'Rows are calm - click one to edit',
        body: 'Each recurring row shows its name and amount. Click one to open its editor.' },
      { path: '/manage', target: '[data-guide="recurring-amount"]', activate: '[data-guide="tab-recurring"]',
        clickFirst: '[data-guide="recurring-name"] > button',
        title: 'Rename and reprice',
        body: 'I opened the first row. Change the Name or the Amount or formula; Vault then asks whether to update the live month too (Yes — update <month> too) or No — from next month only.' },
      { path: '/manage', target: '[data-guide="recurring-ai"]', activate: '[data-guide="tab-recurring"]',
        clickFirst: '[data-guide="recurring-name"] > button',
        title: 'Redo the AI, merge, sync',
        body: '✨ Redo from the name re-derives the category and Spend Map place, ⇄ Merge another in folds a second recurring item into this one, and ↻ Sync into months copies everything into the current and prepared months.' },
      { path: '/manage', target: null,
        title: 'Tax flags live on the expense',
        body: 'Flag a deductible with the Tax-deductible (SARS) tick on its detail card (under Change) and pick its bucket under More options. It lands in your Tax pack, under Manage → 🧾 Tax.' },
        ],
  },
  // #280 — why planned income is logged, and the four ways to schedule it.
  {
    id: 'planned-income',
    title: 'Planned income',
    blurb: 'Why you log money before it lands, and how to schedule it.',
    steps: [
      { path: '/manage', target: '[data-guide="tab-recurring"]', activate: '[data-guide="tab-recurring"]',
        title: 'Log income before it lands',
        body: 'Add your salary or any money you expect as planned income. Then the month knows what is coming, and the leftover per bank is right before the money even arrives.' },
      { path: '/manage', target: null,
        title: 'Tick it when it arrives',
        body: 'Planned income shows as coming. When it lands, tick it received \u2014 the bank moves from "coming" to "in", and your leftover stays honest.' },
      { path: '/manage', target: null,
        title: 'Weekly, monthly, per visit or once',
        body: 'Schedule it the way it really comes in: weekly on chosen days, monthly on a day, per visit (link it to a place on the Spend Map and it logs each time you are there), or once \u2014 just this month, no repeat. Expenses schedule the same way.' },
      { path: '/manage', target: null,
        title: 'Coming from your own bank?',
        body: 'If the money moves out of one of your own banks, choose it under "Make it a transfer (out of\u2026)". It becomes a transfer, not new income, so nothing is counted twice.' },
    ],
  },
  {
    id: 'payday',
    title: 'The payday hand-off',
    blurb: 'What happens when your salary lands.',
    steps: [
      { path: '/app', target: '[data-guide="cycle-note"]',
        title: 'Your month starts on YOUR terms',
        body: 'A fixed payday, or the moment your salary is ticked received - you chose in setup, changeable any time. Paid late? The month waits with you.' },
      { path: '/app', target: '[data-guide="monthly-check"]',
        title: 'Monthly Check does the thinking',
        body: 'Each month Vault reads your budget and tells you what matters - saved, overspent, which bank is short - and the payday hand-off is one confirm.' },
      { path: '/app', target: '[data-guide="bank-cards"]',
        title: 'One master salary, paid-in checks per bank',
        body: 'Your master salary counts once globally; each bank shows what has landed. Tick the salary received and the new month can begin.' },
      { path: '/app', target: null,
        title: 'Money from the master is a CHOICE',
        body: 'When you add income on another bank, the + sheet toggles between "From \u2605 <master bank> \u2014 deduct it there" and "New money \u2014 master untouched". Receiving a planned income later, Receive now\u2026 (amount \u00b7 landing bank \u00b7 deduct from \u2605 master) asks the same - so balances never double-count.' },
        ],
  },
  {
    id: 'savings',
    title: 'The 🐷 savings pot',
    blurb: 'Deposits, withdrawals, the baseline — and honest leftovers.',
    steps: [
      { path: '/app', clickFirst: '[data-guide="add-expense"]', activate: '[data-guide="add-kind-savings"]', target: '[data-guide="add-savings-amount"]', fill: '500',
        title: 'Savings moves real money',
        body: 'From the +, Savings mode moves money into or out of your pot against your savings bank. I set a sample R500.' },
      { path: '/app', clickFirst: '[data-guide="add-expense"]', target: '[data-guide="add-kind-savings"]',
        title: 'Deposit or withdraw',
        body: 'A deposit leaves your savings bank and grows the pot; a withdrawal returns to the bank. No phantom balances - the money always moves for real.' },
      { path: '/app', target: '[data-guide="savings-pot-card"]',
        title: 'The Savings pot card',
        body: 'The Savings pot card shows what is set aside in your savings bank, with Move in, Withdraw and Ledger & target. To record what was banked before Vault, open Manage \u2192 \ud83d\udc37 Savings, tap "baseline R\u2026" under the pot and Set baseline - every movement shows from there.' },
        ],
  },
  {
    id: 'month-plan',
    title: 'Plan a month ahead + Simulate',
    blurb: 'Where each bank opens, what goes out, and a what-if.',
    steps: [
      { path: '/app', target: null,
        title: 'Plan the month before it starts',
        body: 'Open a month ahead of the one you are in (prepare next month, then pick it) and a Plan card appears under the forecast. Nothing on it is saved - it is a planning sheet.' },
      { path: '/app', target: '[data-guide="month-plan-carried"]',
        title: 'Where the opening came from',
        body: 'This line names the month the opening balances were carried from, so you always know what "Carried" means.' },
      { path: '/app', target: '[data-guide="month-plan-table"]',
        title: 'Carried, In, Out, Left - per bank',
        body: 'Carried is the opening balance brought forward from last month. In is carried plus planned income and money moved in from your other banks; Out is planned expenses and transfers out; Left is what remains. The total row adds them up, with transfers cancelling out.' },
      { path: '/app', clickFirst: '[data-guide="month-plan-simulate"]', target: '[data-guide="month-plan-sim-result"]',
        title: 'Simulate: tick what happens',
        body: 'Tick an income as received or an expense as paid and this shows what would then be in each bank - carried plus only the ticked lines. Tick all or Reset any time; ticks are never saved.' },
      { path: '/app', target: '[data-guide="expense-row"]',
        title: 'Paid twice? Count it for next month',
        body: 'Paid a bill twice by mistake this month? Open the expense, go to More options and tap Paid twice? Count it for next month. Next month\'s line is marked paid and nothing more leaves your bank then - the money stays spent this month. Undo paid ahead reverses it.' },
    ],
  },
  {
    id: 'payday-handoff',
    title: 'New month: the hand-off dialog',
    blurb: 'Keep in bank or Bank it, and why the actual balance is the opening.',
    steps: [
      { path: '/app', target: null,
        title: 'The "Roll over" dialog',
        body: 'When your salary lands (or payday arrives) Vault asks \ud83d\udcb0 Roll over to <next month>? before anything changes: What moved you here, Settle <last month> expenses, and each bank\u2019s opening balance. Nothing commits yet - not until you confirm.' },
      { path: '/app', target: null,
        title: 'Actual balance now = the opening',
        body: 'Each bank shows its Actual balance now, filled in for you. That balance already holds last month\u2019s leftover, so it IS the new month\u2019s opening - correct it if your banking app says otherwise. Starts month at shows the result.' },
      { path: '/app', target: null,
        title: 'Keep in bank, or Bank it \u2192 savings',
        body: '\u2713 Keep in bank leaves the leftover where it is, so the month starts at the full balance. Bank it \u2192 savings moves last month\u2019s leftover into your savings pot, and Starts month at drops by that amount - real money moved, never a phantom.' },
      { path: '/app', target: null,
        title: 'One confirm - or Review first',
        body: '\u2713 Settle <last month> & start <next month> runs it once: unpaid lines close, untapped income carries, recurring expenses copy in with usage reset. Review first closes the dialog so you can look around; it comes back until you confirm. Under Each month, roll over\u2026 choose Ask me first or Do it automatically.' },
      { path: '/app', target: '[data-guide="month-plan-carried"]',
        title: 'You will see it on the Plan card',
        body: 'Planning a month ahead, the Carried column and this line show those openings came from the month before.' },
    ],
  },
  // ——— Overlay-scoped tours: with one of these OPEN, the guide talks about
  //     exactly what's on screen, nothing else.
  {
    id: 'item-detail',
    title: 'The full record',
    blurb: 'Everything about one income or transfer line, one place.',
    // Narrative only: a spotlight step closes overlays, and this tour talks
    // about the open full-record overlay itself.
    steps: [
      { path: '/app', target: null,
        title: 'The full record',
        body: 'This card opens from an income row\u2019s "Full record \u2014 salary, recurring, delete \u25b8" link, or from a transfer line. Expenses keep everything on their own detail card in the row: Record what you spent, Change (with the Tax-deductible (SARS) tick) and Logged this month.' },
      { path: '/app', target: null,
        title: 'Amount or formula - and how far it saves',
        body: 'Type a number or a formula (200*31). Save \u00b7 this month keeps it here; Save \u00b7 everywhere writes the recurring setup and every current month with this name, after asking you to confirm.' },
      { path: '/app', target: null,
        title: 'Income has its own doors',
        body: 'Not received yet: Receive now\u2026 (amount \u00b7 landing bank \u00b7 deduct from \u2605 master). Received: "Deduct from \u2605 master" flips that pairing. "\u21bb Every month from now \u2014 add to recurring" promotes a one-off into recurring. salary? marks the salary that starts your new month.' },
      { path: '/app', target: null,
        title: 'Delete asks how far',
        body: 'Delete\u2026 on a recurring line offers Only <month>; <month> + months ahead (the recurring setup stays, \u21bb Sync can bring it back); or Everywhere + stop recurring. A one-off just asks Delete it.' },
      { path: '/app', target: null,
        title: 'A note, yours or AI\u2019s',
        body: 'Note \u2014 yours or AI\u2019s, edit freely. The box at the bottom is yours - it saves as soon as you click away.' },
        ],
  },
  {
    id: 'add-sheet',
    title: 'The + sheet',
    blurb: 'Expense, income or savings — one sheet.',
    steps: [
      { path: '/app', clickFirst: '[data-guide="add-expense"]', target: '[data-guide="add-kind"]',
        title: 'Three modes, one sheet',
        body: 'Expense, Income and Savings all live here. Press Next and I will walk the expense fields - every one is overridable.' },
      { path: '/app', clickFirst: '[data-guide="add-expense"]', activate: '[data-guide="add-kind-expense"]', target: '[data-guide="add-title"]', fill: 'Coffee',
        title: 'Name it - AI does the rest',
        body: 'I typed one word. AI turns it into a category from YOUR tabs, a note, the recurring hint and the likely bank. Watch it fill on the next step.' },
      { path: '/app', clickFirst: '[data-guide="add-expense"]', activate: '[data-guide="add-ai-run"]', target: '[data-guide="add-note"]',
        title: 'Watch the AI fill it live',
        body: 'From just "Coffee" the AI writes this note, picks the category and guesses the bank - live, and all yours to change.' },
      { path: '/app', clickFirst: '[data-guide="add-expense"]', target: '[data-guide="add-scan"]',
        title: 'Or scan - or upload - a receipt',
        body: 'Photograph a till slip or a quote, OR upload a receipt you already have. Either way AI reads the shop, amount, date and category, shows them for you to confirm, then asks: just this month, or recurring?' },
      { path: '/app', clickFirst: '[data-guide="add-expense"]', target: '[data-guide="add-amount"]', fill: '200*31',
        title: 'A plain amount - or a formula',
        body: '200*31 pays off day by day with a stepper; or type a flat total. Human shapes like R 1 000,00 work too.' },
      { path: '/app', clickFirst: '[data-guide="add-expense"]', target: '[data-guide="add-tax"]',
        title: 'Flag it for SARS right here',
        body: 'Tick Tax-deductible and pick a bucket as you log it - it flows straight into your Tax pack. Optional, always yours.' },
      { path: '/app', clickFirst: '[data-guide="add-expense"]', target: '[data-guide="add-save"]',
        title: 'Save - and it lands this month',
        body: 'Income and Savings work the same way from the mode buttons up top. One sheet, everything.' },
        ],
  },
  {
    id: 'savings-pot',
    title: 'The pot overlay',
    blurb: 'Deposits and withdrawals, straight into the record.',
    steps: [
      { path: '/app', target: null,
        title: 'Two ways, real money',
        body: 'Pick \ud83d\udcb0 Deposit or \ud83d\udcb8 Withdraw, type the amount and an optional note, then Deposit into the pot or Withdraw from the pot. A deposit leaves your savings home bank and grows the pot; a withdrawal returns to it.' },
      { path: '/app', target: null,
        title: 'The baseline is yours',
        body: 'Tap "past savings R\u2026" under the pot total to set what was already banked before Vault, then Set baseline. The pot always equals the baseline plus every movement. Manage \u2192 \ud83d\udc37 Savings has the same baseline, plus the ledger and a target.' },
        ],
  },
  {
    id: 'logged-items',
    title: 'Logged items, Recount & leftover',
    blurb: 'Everything you spend is one thing you can see and delete.',
    steps: [
      { path: '/app', target: '[data-guide="leftover"]',
        title: 'Leftover is the honest truth',
        body: 'Your leftover is your bank balance minus what is still to pay - and every logged item keeps it real.' },
      { path: '/app', target: '[data-guide="expense-row"]',
        title: 'Every rand is a logged item',
        body: 'Formula units, standing events and one-off spends are all the same thing - a logged entry you can see, name and delete.' },
      { path: '/app', target: '[data-guide="add-expense"]',
        title: 'Add a spend - money, at any place',
        body: 'From the +, add an amount to an existing expense: a plain number is money, at any shop or address, with a note.' },
      { path: '/app', target: '[data-guide="expense-row"]',
        title: 'Recount recalculates from your logs',
        body: 'On the detail card, Change \u2192 More options \u2192 Recount from logged rebuilds spent from exactly what is logged: Clear to logs, or Log the missing when some spend was never logged.' },
      { path: '/app', target: '[data-guide="expense-row"]',
        title: 'Make it a spend on another',
        body: 'Under More options, Merge into another expense turns this whole expense into one logged spend on the expense you choose, then removes it - the money follows.' },
      { path: '/app', target: '[data-guide="monthly-check"]',
        title: 'Done means done',
        body: 'Tick an expense done and its daily event stops - no more auto-logs. Untick to bring it back.' },
        ],
  },
  {
    id: 'insights',
    title: 'Graphs & insights',
    blurb: 'See where the money actually goes.',
    steps: [
      { path: '/insights', target: '[data-guide="spend-insights"]',
        title: 'Your spending, drawn out',
        body: 'Spending insights charts your month in three lists: By category, Top expenses, and By shop / place (with how often you went). It reads straight from what you logged.' },
      { path: '/insights', target: '[data-guide="spend-insights"]',
        title: 'Category, expense and place at a glance',
        body: 'Bars show which categories eat the most, which expenses cost the most, and the shops you spend at most. Link expenses to shops on the Map to fill By shop / place.' },
      { path: '/insights', target: '[data-guide="spend-insights"]',
        title: 'It sharpens as you log',
        body: 'Nothing to set up - it just reflects what you have recorded, and gets richer every time you log.' },
        ],
  },
  {
    id: 'map-link',
    title: 'Link a spend to a shop',
    blurb: 'Attach an expense to a place.',
    steps: [
      { path: '/app', target: '[data-guide="map-search"]', open: 'clear',
        title: 'Search or pin the shop',
        body: 'Type a shop or address to find it, or use the 🏠 button to pin exactly where you stand. Pick it to save the place.' },
      { path: '/app', target: '[data-guide="map-link-panel"]', open: 'link',
        title: 'Pick the expense that fits',
        body: 'The saved shop lists your expenses — formula ones show their R-per-unit. Choose the one that belongs here.' },
      { path: '/app', target: '[data-guide="map-link-panel"]', open: 'link',
        title: '🏠 Its home — the shop is its home',
        body: '🏠 Its home makes this shop the expense\'s constant HOME, recurring-linked so future months place it themselves.' },
      { path: '/app', target: '[data-guide="map-repeat"]', open: 'link-advanced',
        title: '📅 Dated & recurring — a dated spend',
        body: '📅 Dated & recurring logs a dated spend here — units for a formula expense, otherwise a rand amount, on any date.' },
      { path: '/app', target: '[data-guide="map-repeat"]', open: 'link-advanced',
        title: 'How often: One-off · Daily · Weekly · Monthly',
        body: 'Set how it repeats, or tick Only when I\'m there — nothing is scheduled; it logs by GPS when your phone detects you at the shop and you approve.' },
    ],
  },
  {
    id: 'map-trip-story',
    title: 'Track a trip & petrol',
    blurb: 'Log an Uber or a drive by GPS.',
    steps: [
      { path: '/app', target: '[data-guide="map-trip"]', open: 'clear',
        title: '🚗 Start the trip',
        body: 'Tap 🚗 and Vault records your GPS route (the green line) as you travel. Keep the tab open during the ride.' },
      { path: '/app', target: '[data-guide="map-trip"]', open: 'clear',
        title: 'End it — and settle the cost',
        body: 'Tap it again to end. Vault asks what the trip cost and settles that against a transport expense — perfect for Ubers and petrol runs.' },
      { path: '/app', target: '[data-guide="map-trips"]', open: 'clear',
        title: '🧭 Replay the road',
        body: 'Tap 🧭 Trips to see every trip with its distance and cost (business trips carry a SARS badge). Pick one and the exact road you drove is traced on the map — so you can see where the money went, not just how much.' },
    ],
  },
  {
    id: 'map-replay',
    title: 'Replay & review a day',
    blurb: 'See any day; confirm what happened.',
    steps: [
      { path: '/app', target: '[data-guide="map-daterange"]', open: 'clear',
        title: '🗓 Pick a day to replay',
        body: 'Use 🗓 in the bar at the top to pick a date, then By place, By expense or All. Events due that day show as "expected", ready to confirm. Whole month takes you back.' },
      { path: '/app', target: '[data-guide="map-expense"]', open: 'expense',
        title: 'Open an expense',
        body: 'Clicking an expense opens its panel — confirm the day\'s visits, add a dated spend, or open its Setup. Everything logged is deletable and reverses fully.' },
      { path: '/app', target: '[data-guide="map-places"]', open: 'clear',
        title: 'This month by place',
        body: 'Each place shows what was spent and units done, plus leftover. Click a shop to rename, move, delete, or unlink an expense (its spend stays).' },
    ],
  },
  {
    id: 'map-events',
    title: 'Shop events & near-shop nudges',
    blurb: 'Let places remind you to log.',
    steps: [
      { path: '/app', clickFirst: '[data-guide="open-map"]', target: '[data-guide="map-events"]',
        title: 'Standing shop events',
        body: 'Open the Spend Map: your recurring shop events list here - a daily coffee, a weekly shop. Each logs and asks you to confirm.' },
      { path: '/app', clickFirst: '[data-guide="open-map"]', target: '[data-guide="map-events"]',
        title: 'One tap logs a day',
        body: 'Set an event once - twice a day, weekly, monthly - and Vault records each day for you to confirm. Pause, resume or stop any time.' },
      { path: '/app', target: '[data-guide="open-map"]',
        title: 'Your phone nudges you when you arrive',
        body: 'Install the mobile app and Vault notices when you are AT a saved shop and offers to log right there - unless it is already settled this month.' },
      { path: '/app', target: '[data-guide="open-map"]',
        title: 'The watch does it too',
        body: 'On Apple Watch and Wear OS the same arrival buzz reaches your wrist, so you can log a spend where you stand.' },
        ],
  },
  {
    id: 'scan',
    title: 'Scan a receipt or quote',
    blurb: 'Photograph it \u2014 AI does the rest.',
    steps: [
      { path: '/app', clickFirst: '[data-guide="add-expense"]', target: '[data-guide="add-scan"]',
        title: 'Snap or upload a receipt or quote',
        body: 'From the +, scan a till slip or a quote with the camera - or upload one you already have. AI reads the shop, amount, date and category.' },
      { path: '/app', clickFirst: '[data-guide="add-expense"]', target: '[data-guide="add-scan"]',
        title: 'It even matches the place',
        body: 'AI works out where you bought from - matching a saved shop on your Spend Map or the address on the slip - so it logs to the right pin.' },
      { path: '/app', clickFirst: '[data-guide="add-expense"]', target: '[data-guide="add-scan"]',
        title: 'Every match is yours to change - by voice or typing',
        body: 'When AI matches an EXISTING expense it groups the receipt into it, and the shop it read shows as the location - both pre-filled so you just confirm. Change either freely: a search box with a mic lets you say or type another expense, or another shop / address. Nothing is locked in.' },
      { path: '/app', clickFirst: '[data-guide="add-expense"]', target: '[data-guide="add-save"]',
        title: 'Approve, then just this month or recurring',
        body: 'Nothing saves until you confirm what AI found; tweak anything, then say whether it repeats.' },
        ],
  },
  {
    id: 'voice',
    title: 'Talk instead of type',
    blurb: 'Say it \u2014 AI writes the row.',
    steps: [
      { path: '/app', target: '[data-guide="add-expense"]',
        title: 'Talk instead of type',
        body: 'Open the + sheet. On an expense, the search box "\ud83d\udd0e \u2026or add it to an EXISTING expense \u2014 type to search" has a mic: say the expense instead of typing it.' },
      { path: '/app', target: null,
        title: 'Say the shop, too',
        body: 'Tap \ud83d\udccd Also link it to a shop and the "Shop name or address (nearest first)" box has its own mic - say the shop instead of typing. Handy after a scan when you want to swap the matched expense or place in one breath. The search boxes on your month, Manage \u2192 Recurring and the Spend Map listen too.' },
        ],
  },
  {
    id: 'power',
    title: 'Power actions',
    blurb: 'Override, reclassify, recount.',
    steps: [
      { path: '/app', target: '[data-guide="expense-row"]',
        title: 'Override the total spent',
        body: 'Open any expense and set the exact total spent by hand when the real spend differs - your leftover recomputes straight away.' },
      { path: '/app', target: '[data-guide="expense-row"]',
        title: 'Reclassify onto another expense',
        body: 'Logged against the wrong line? Under More options, Merge into another expense logs its value as one spend on the expense you pick and removes this one - the money moves with it, no delete-and-retype.' },
      { path: '/app', target: '[data-guide="expense-row"]',
        title: 'Recount rebuilds the truth',
        body: 'Recount from logged rebuilds spent from exactly what is logged - Clear to logs, or Log the missing.' },
      { path: '/app', target: null,
        title: 'Ask the guides',
        body: 'Not sure where something lives? Open the guide book and type a question in plain words, like "how do I pick the bank for a spend". Vault answers from these guides and offers the walkthrough that shows you.' },
        ],
  },
  {
    id: 'account',
    title: 'Account, WhatsApp & your data',
    blurb: 'Reports, POPIA, help and your watch.',
    steps: [
      { path: '/account', target: '[data-guide="account-whatsapp"]',
        title: 'WhatsApp notifications',
        body: 'Optional: add your number and switch it on for support replies and helpful nudges from Vault\'s AI on WhatsApp. Off by default - nothing sends unless you switch it on.' },
      { path: '/account', target: '[data-guide="account-data"]',
        title: 'Your data is yours',
        body: 'Under Your data: \u2b07 Export (JSON), \u2b07 Export (CSV), \ud83e\uddfe All receipts (zip), or Delete my account - free, unlimited and no support ticket, under POPIA. Delete cannot be undone.' },
      { path: '/account', target: '[data-guide="account-invite"]',
        title: 'Invite friends, both get free days',
        body: 'Share your link to WhatsApp; when a friend joins you each get extra free days.' },
      { path: '/account', target: '[data-guide="account-watch"]',
        title: 'Pair your Apple Watch or Wear OS',
        body: 'Generate a code here, then on the watch tap Link with a phone code and enter it.' },
        ],
  },
  {
    id: 'essentials',
    title: 'What matters vs what is optional',
    blurb: 'The 20% that makes Vault sing.',
    steps: [
      { path: '/app', target: '[data-guide="leftover"]',
        title: 'Essential: keep leftover honest',
        body: 'The one habit that matters - log or import what you spend, and set each bank\'s real balance now and then. Do just that and the headline leftover stays true.' },
      { path: '/app', target: '[data-guide="add-expense"]',
        title: 'Optional: let AI and import do the typing',
        body: 'Type one word and tab away, or import a statement and approve. Optional, but it turns ten minutes of admin into ten seconds.' },
      { path: '/app', target: '[data-guide="open-manage"]',
        title: 'Power moves: formulas, shops, recurring, tax',
        body: 'Formulas pay things off day by day, shops remind you, recurring seeds each month, and the Tax-deductible (SARS) tick on an expense flags deductibles - your Tax pack lives under Manage \u2192 \ud83e\uddfe Tax. Each makes next month easier.' },
      { path: '/app', target: '[data-guide="monthly-check"]',
        title: 'What you can skip',
        body: 'Do not fuss over perfect categories or log every rand by hand; payday resets the month. A few honest numbers beat a spreadsheet you dread.' },
        ],
  },
  {
    id: 'tax',
    title: 'Tax pack for SARS',
    blurb: 'Flag deductibles as you go; hand your accountant a clean pack.',
    steps: [
      { path: '/tax', target: '[data-guide="tax-totals"]',
        title: 'Your tax pack in one place',
        body: 'Every expense you flag tax-deductible for the chosen SARS tax year, grouped into SARS buckets in rand, with your travel logbook. This organises what you logged - you and your accountant decide what qualifies.' },
      { path: '/tax', target: '[data-guide="tax-year"]',
        title: 'Pick the tax year',
        body: 'South Africa runs 1 March to end February. Choose the year and the whole pack rebuilds for it.' },
      { path: '/tax', target: '[data-guide="tax-export"]',
        title: 'Print or export for your accountant',
        body: 'Print or Save as PDF, or export a CSV of every line plus the travel logbook - ready to hand over.' },
      { path: '/app', target: null,
        title: 'Where the numbers come from',
        body: 'Open any expense (click a row on your dashboard) and tap Change: tick Tax-deductible (SARS), then pick the tax bucket under More options - it flows straight into this pack. The + sheet has the same tick and a Pick a SARS bucket list as you log.' },
      { path: '/app', target: null,
        title: 'Flag it on the map too',
        body: 'On the Spend Map, an expense opens the same detail card, and a shop event\'s editor has its own Tax-deductible tick and SARS bucket - flag a deductible right where you spend it. The travel logbook fills itself from trips you track.' },
      { path: '/app', target: null,
        title: 'Organisation, not advice',
        body: 'Vault sorts and totals what you recorded, in rand. It is not tax advice and does not decide what qualifies - that is between you and your accountant. Keep a receipt on each expense and it rides along in the pack.' },
    ],
  },
  {
    id: 'import-statement',
    title: 'Import a bank statement',
    blurb: 'Upload a statement, review what Vault found, approve.',
    steps: [
      { path: '/app', target: '[data-guide="bank-cards"]',
        title: 'Start from the bank',
        body: 'A statement imports into one bank card, so each import stays tied to that account. Press Next and I will open your first bank.' },
      { path: '/app', clickFirst: '[data-guide="bank-cards"] > div > button', target: '[data-guide="import-statement"]',
        title: '\ud83d\udce5 Import statement',
        body: 'This opens Import a <bank> statement. Pick a PDF or Excel (.xlsx or .xls) file and press Upload & review - a locked PDF asks for its password first. Re-importing the same statement is always safe.' },
      { path: '/app', target: null,
        title: 'Review, then apply',
        body: 'Nothing saves on upload. Vault sorts the lines into Looks paid, Money in, Formula groups, Recurring, Price changes, New spending and Already applied, and counts Approved as you tick. Apply saves only what you approved.' },
    ],
  },
];

/**
 * `simple` (Simple view, #476): the 📖 stays bottom-left as Help — ask a
 * question by typing or out loud and hear the answer — but the walkthroughs
 * themselves belong to the full view (Simple view spec §7: no replay in
 * Simple view), so the sheet offers "See every guide", which switches to
 * Everything and opens them there. No nudge, no autoplay, no For you.
 */
export default function Guide({ simple = false }) {
  const { setMode } = useViewMode();
  const [open, setOpen] = useState(false);       // tour picker
  const [tour, setTour] = useState(null);        // active tour
  const [idx, setIdx] = useState(0);
  const [rect, setRect] = useState(null);        // spotlight rectangle
  const [nudge, setNudge] = useState(false);     // every-now-and-then reminder
  const [recs, setRecs] = useState(null);        // #9 AI-curated "For you" picks
  const [q, setQ] = useState('');                // picker search box
  const [ask, setAsk] = useState(null);          // AI answer to a typed question {answer, picks, ai}
  const [voice, setVoice] = useState('device');  // guide voice engine: device | ai
  const [readAloud, setReadAloud] = useState(false); // speak each bubble as it opens
  const [voiceAsked, setVoiceAsked] = useState(false); // last question was spoken → speak the answer
  const [simpleGuideOn, setSimpleGuideOn] = useState(false); // Simple view's own 4-step guide is up
  useEffect(() => {
    const on = (e) => setSimpleGuideOn(!!e.detail);
    window.addEventListener('vault-simple-guide', on);
    return () => window.removeEventListener('vault-simple-guide', on);
  }, []);
  const [done, setDone] = useState(() => {        // guides you have finished → ✓
    try { return new Set(JSON.parse(localStorage.getItem('vault_guides_done') || '[]')); }
    catch { return new Set(); }
  });
  const markDone = (id) => setDone((prev) => {
    const nx = new Set(prev); nx.add(id);
    try { localStorage.setItem('vault_guides_done', JSON.stringify([...nx])); } catch { /* ok */ }
    return nx;
  });

  // #9 AI-curated picker: when the GENERAL picker opens, ask the backend to rank
  // the tours against this user's setup state (banks/expenses/formulas/map pins/
  // tax). Best-effort — the picker shows the full list regardless of the result.
  useEffect(() => {
    if (!open || simple) { setRecs(null); return; }
    const ctx = document.querySelector('[data-guide-context]')
      ?.getAttribute('data-guide-context');
    if (ctx) return;                       // context-scoped picker: no "For you"
    const overlayOnly = ['item-detail', 'add-sheet', 'savings-pot'];
    const list = visibleTours().filter((t) => !overlayOnly.includes(t.id))
      .map((t) => ({ id: t.id, title: t.title }));
    let alive = true;
    api('/budget/guides/recommend/', { method: 'POST', body: { tours: list } })
      .then((d) => { if (alive) setRecs(Array.isArray(d?.recommended) ? d.recommended : []); })
      .catch(() => { if (alive) setRecs([]); });
    return () => { alive = false; };
  }, [open]);

  // Ask the guides: a question of three words or more ("how do I pick the
  // bank for a spend") goes to /budget/guides/ask/ with every tour's own step
  // text, so the answer can only come from what the guides actually say.
  useEffect(() => {
    const question = q.trim();
    if (!open || question.split(/\s+/).length < 3) { setAsk(null); return undefined; }
    let alive = true;
    const t = setTimeout(() => {
      setAsk({ loading: true });
      const tours = visibleTours().map((x) => ({ id: x.id, title: x.title, blurb: x.blurb,
        text: (x.steps || []).map((st) => `${st.title || ''}: ${st.body || ''}`).join(' ').slice(0, 900) }));
      api('/budget/guides/ask/', { method: 'POST', body: { question, tours } })
        .then((d) => { if (alive) { setAsk(d || null); if (voiceAsked && d?.answer) speakText(d.answer, voice); } })
        .catch(() => { if (alive) setAsk(null); });
    }, 600);
    return () => { alive = false; clearTimeout(t); };
  }, [q, open]);

  // "See every guide" from Simple view's Help opens the picker once the full view is up.
  useEffect(() => {
    const on = () => setOpen(true);
    window.addEventListener('vault-open-guide', on);
    return () => window.removeEventListener('vault-open-guide', on);
  }, []);

  // Every-now-and-then nudge: at most once a week, 20s into a session, and
  // never while a tour or the picker is up. Opening the guide counts as
  // "reminded" too.
  useEffect(() => {
    try {
      const last = Number(localStorage.getItem('vault_guide_nudge') || 0);
      if (Date.now() - last > 12 * 36e5) {   // ~12h between reminders
        const t = setTimeout(() => setNudge(true), 6000);   // a few seconds in
        return () => clearTimeout(t);
      }
    } catch { /* storage blocked — skip the nudge */ }
  }, []);

  // Auto-play ONCE for a brand-new user: a moment after the app first settles,
  // run the Welcome tour, then stamp a flag so it never replays. First-run
  // onboarding; every other tour stays on-demand behind the launcher. Local flag
  // now; the server guides_autoplayed field makes it once-per-account.
  useEffect(() => {
    let alive = true;
    if (simple) return undefined;
    try { if (localStorage.getItem('vault_autoplay_done')) return undefined; } catch { return undefined; }
    const w = visibleTours().find((t) => t.id === 'welcome');
    if (!w) return undefined;
    (async () => {
      // Prefer the ACCOUNT flag so a user who saw the Welcome tour on another
      // device (or on mobile) is not re-onboarded here; fall back to local-first
      // if /auth/me is unavailable.
      try {
        const me = await api('/auth/me/');
        if (!alive) return;
        if (me && me.guides_autoplayed) {
          try { localStorage.setItem('vault_autoplay_done', '1'); } catch { /* ok */ }
          return;
        }
      } catch { /* offline — play locally */ }
      if (!alive) return;
      setTimeout(() => {
        if (!alive) return;
        setTour(w); setIdx(0); setOpen(false);
        try { localStorage.setItem('vault_autoplay_done', '1'); } catch { /* ok */ }
        api('/budget/guides/seen/', { method: 'POST' }).catch(() => {});
      }, 1200);
    })();
    return () => { alive = false; };
  }, []);
  const snoozeNudge = () => {
    setNudge(false);
    try { localStorage.setItem('vault_guide_nudge', String(Date.now())); } catch { /* ok */ }
  };
  // A nudge, not a nag: once the reminder appears it quietly hides itself after
  // a few seconds and stamps the time, so it waits ~12h before reminding again.
  useEffect(() => {
    if (!nudge) return undefined;
    const t = setTimeout(() => {
      setNudge(false);
      try { localStorage.setItem('vault_guide_nudge', String(Date.now())); } catch { /* ok */ }
    }, 7000);
    return () => clearTimeout(t);
  }, [nudge]);
  const nav = useNavigate();
  const loc = useLocation();
  const clickArm = useRef(null);
  const elRef = useRef(null);
  const openedByGuide = useRef(false);

  const step = tour?.steps[idx] || null;
  // Read aloud: speak each bubble as it opens; stop when the guide ends.
  useEffect(() => {
    stopSpeaking();
    if (!tour || !step) return;
    if (readAloud) speakText(`${step.title}. ${step.body}`, voice);
    return () => stopSpeaking();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [tour, idx, readAloud, voice]);

  // Position the spotlight on the step's target (retry while the page renders).
  useEffect(() => {
    elRef.current = null;
    setRect(null);
    if (!step) return;
    if (step.path && loc.pathname !== step.path) { nav(step.path); return; }
    let cancelled = false;
    const timers = new Set();
    /** Cancel delayed UI actions when their originating step is no longer active. */
    const schedule = (callback, delay) => {
      const timer = setTimeout(() => {
        timers.delete(timer);
        if (!cancelled) callback();
      }, delay);
      timers.add(timer);
    };
    // A dashboard-TARGET step (has a target, no sheet to open, no map panel)
    // starts CLEAN: close any overlay a previous step left open so it never
    // spotlights the dashboard with the add-sheet / pot / map on top. Narrative
    // steps (target null) are skipped, so a map tour's narrative interludes
    // keep the map open.
    if (step.target && !step.clickFirst && !step.open) window.dispatchEvent(new CustomEvent('vault-close-overlays'));
    if (step.open) {
      // The Spend Map is a modal that only MOUNTS (and starts listening for
      // these events) once open, so open it first if closed, then fire the panel
      // event after it mounts — otherwise the map tours never load the map.
      const mapUp = !!document.querySelector('[data-guide="map-search"]');
      if (!mapUp) {
        const opener = document.querySelector('[data-guide="open-map"]');
        if (opener) { openedByGuide.current = true; opener.click(); }
      }
      const fire = () => window.dispatchEvent(new CustomEvent('vault-map-tour', { detail: { show: step.open } }));
      // Fire a few times: immediate (map already up), after it mounts, and
      // once more after the shop/expense data loads — so link/expense panels
      // (which need a place + expense) open even when the data lands late.
      if (mapUp) fire();
      schedule(fire, 350);
      schedule(fire, 800);
    }
    // Driven tours: click a control to OPEN the real UI (the + Add sheet, a tab).
    // RETRY until it exists — the sheet/tab renders async after a click or a route
    // change, so a one-shot click missed it (Savings/Income mode, the Recurring
    // tab after navigating to /manage). Stops the instant the target appears.
    if (step.clickFirst && (!step.target || !document.querySelector(step.target))) {
      let ct = 0;
      const openIt = () => {
        if (step.target && document.querySelector(step.target)) return;
        const opener = document.querySelector(step.clickFirst);
        if (opener) { openedByGuide.current = true; opener.click(); }
        if (ct++ < 20 && (!step.target || !document.querySelector(step.target))) schedule(openIt, 150);
      };
      openIt();
    }
    // Driven tours: click a control to SWITCH state (Expense→Savings, a tab).
    // Retry until the control renders, then click once — the fix for the mode
    // and tab steps that silently did nothing when the control lagged behind.
    if (step.activate) {
      let at = 0;
      const act = () => {
        const btn = document.querySelector(step.activate);
        if (btn) { btn.click(); return; }
        if (at++ < 20) schedule(act, 150);
      };
      act();
    }
    let tries = 0;
    const find = () => {
      if (cancelled) return;
      const el = step.target ? document.querySelector(step.target) : null;
      if (el) {
        el.scrollIntoView({ block: 'center' });
        elRef.current = el;
        const r = el.getBoundingClientRect();
        setRect({ top: r.top, left: r.left, width: r.width, height: r.height });
        // Keep the ring ON target as the two-pane panel/map settle and late
        // content shifts it (the 'ring on an empty box' bug) — re-measure a few
        // times without re-scrolling, so it follows the real control into place.
        let rm = 0;
        const remeasure = () => {
          if (cancelled) return;
          const rr2 = el.getBoundingClientRect();
          if (rr2.width && rr2.height) setRect({ top: rr2.top, left: rr2.left, width: rr2.width, height: rr2.height });
          if (rm++ < 9) schedule(remeasure, 200);
        };
        schedule(remeasure, 200);
        // Driven tours: pre-fill a demo value into the highlighted input the React
        // way (native setter + input event), only while it is still empty.
        if (step.fill != null && (el.tagName === 'INPUT' || el.tagName === 'TEXTAREA')) {
          const proto = el.tagName === 'TEXTAREA'
            ? window.HTMLTextAreaElement.prototype : window.HTMLInputElement.prototype;
          const setter = Object.getOwnPropertyDescriptor(proto, 'value').set;
          try {
            setter.call(el, step.fill); el.dispatchEvent(new Event('input', { bubbles: true }));
            requestAnimationFrame(() => {
              if (cancelled) return;
              const rr = el.getBoundingClientRect();
              setRect({ top: rr.top, left: rr.left, width: rr.width, height: rr.height });
            });
          } catch { /* ignore */ }
        }
        if (step.advanceOn === 'click') {
          clickArm.current = (e) => {
            if (el.contains(e.target)) next();
          };
          document.addEventListener('click', clickArm.current, true);
        }
      } else if (step.target && tries++ < 20) {
        schedule(find, 150);
      } else if (step.target) {
        // Role-aware (hub guides spec): a step whose target control is NOT on this
        // screen/role is DROPPED — skip to the next step, never an empty bubble.
        next();
      } else {
        elRef.current = null;
        setRect(null);   // narrative step (no target): centre bubble, no spotlight
      }
    };
    if (step.activate || step.clickFirst) {
      requestAnimationFrame(() => requestAnimationFrame(find));
    } else {
      find();
    }
    return () => {
      cancelled = true;
      timers.forEach(clearTimeout);
      if (clickArm.current) {
        document.removeEventListener('click', clickArm.current, true);
        clickArm.current = null;
      }
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [step, loc.pathname]);

  // Keep the spotlight glued to its target as the page (or an inner panel like
  // the map overlay) scrolls or resizes — without this the highlight drifts off.
  useEffect(() => {
    if (!step || !step.target) return undefined;
    const reflow = () => {
      const el = elRef.current;
      if (!el) return;
      const r = el.getBoundingClientRect();
      setRect({ top: r.top, left: r.left, width: r.width, height: r.height });
    };
    window.addEventListener('scroll', reflow, true);
    window.addEventListener('resize', reflow);
    return () => {
      window.removeEventListener('scroll', reflow, true);
      window.removeEventListener('resize', reflow);
    };
  }, [step]);

  /** Start a tour without taking ownership of an already-open contextual sheet. */
  const start = (t) => {
    openedByGuide.current = false;
    // Overlay tours narrate the overlay that is open, so they must not close it.
    if (!['item-detail', 'savings-pot'].includes(t.id)) window.dispatchEvent(new CustomEvent('vault-close-overlays'));
    window.dispatchEvent(new CustomEvent('vault-map-tour', { detail: { show: 'clear' } }));
    setTour(t); setIdx(0); setOpen(false);
  };
  /** Close guide-owned sheets and stop playback without saving their contents. */
  const stop = useCallback(() => {
    if (openedByGuide.current) window.dispatchEvent(new CustomEvent('vault-close-overlays'));
    openedByGuide.current = false;
    window.dispatchEvent(new CustomEvent('vault-map-tour', { detail: { show: 'clear' } }));
    setTour(null); setIdx(0); setRect(null);
    stopSpeaking();
  }, []);
  const next = () => {
    if (idx + 1 < tour.steps.length) { setIdx(idx + 1); return; }
    markDone(tour.id);   // completed → ✓ in the picker
    stop();
  };

  // Bubble placement: below the spotlight when it fits, else above, else centre.
  // Always cap the height to the room available and let the body scroll, so long
  // steps (the Spend Map story) are fully readable instead of running off-screen.
  const bubbleW = Math.min(340, window.innerWidth - 32);
  const bubbleStyle = (() => {
    const vw = window.innerWidth, vh = window.innerHeight;
    if (!rect) return { top: '40%', left: '50%', transform: 'translateX(-50%)', width: bubbleW, maxHeight: '70vh' };
    const clampL = (x) => Math.min(Math.max(x, 16), vw - bubbleW - 16);
    const below = vh - (rect.top + rect.height), above = rect.top;
    if (below >= 220) { const top = rect.top + rect.height + 12;
      return { top, left: clampL(rect.left), width: bubbleW, maxHeight: vh - top - 16 }; }
    if (above >= 180) {
      // hug the target: anchor the bubble's BOTTOM just above it (grows upward)
      return { bottom: vh - rect.top + 12, left: clampL(rect.left), width: bubbleW, maxHeight: rect.top - 24 }; }
    // Tall/centred target (a panel): sit BESIDE it, whichever side has room.
    if (rect.left >= bubbleW + 28)
      return { top: 16, left: rect.left - bubbleW - 12, width: bubbleW, maxHeight: vh - 32 };
    if (vw - (rect.left + rect.width) >= bubbleW + 28)
      return { top: 16, left: rect.left + rect.width + 12, width: bubbleW, maxHeight: vh - 32 };
    return { top: vh - 210, left: clampL(rect.left), width: bubbleW, maxHeight: 194 };
  })();

  return (
    <>
      {/* launcher — book icon above the reporter's ? */}
      {/* z-[85]: above the Manage drawer (z-50) so the guide stays usable
          inside it; the drawer's own ask-overlay (z-[60]) still wins. */}
      {!tour && !(simple && simpleGuideOn) && (
        <button title={simple ? 'Help — ask a question' : 'Guide — see how everything works'}
                className={`fixed bottom-20 left-6 z-[85] w-11 h-11 rounded-full border
                            bg-panel text-lg transition ${
                              nudge ? 'border-vpurple animate-pulse' : 'border-edge hover:border-vblue'}`}
                onClick={() => { snoozeNudge(); setOpen(true); }}>
          📖
        </button>
      )}

      {/* The now-and-then reminder bubble next to the book. */}
      {nudge && !tour && !open && !simple && (
        <div className="fixed bottom-20 left-20 z-[85] card !p-4 w-72 shadow-2xl border-vpurple/50">
          {/* the pointer: a rotated square peeking out of the bubble's left
              edge, aimed straight at the 📖 button */}
          <span className="absolute -left-1.5 bottom-4 w-3 h-3 rotate-45 bg-panel
                           border-l border-b border-vpurple/50" />
          <p className="text-sm">
            <span className="font-semibold">Rusty on anything?</span>{' '}
            The Guide walks the real screens — formulas, imports, the map,
            payday. Two minutes, worth it.
          </p>
          <div className="mt-2 flex gap-3">
            <button className="btn-primary !py-1 !px-3 text-sm"
                    onClick={() => { snoozeNudge(); setOpen(true); }}>
              Open the guide
            </button>
            <button className="text-xs text-slate-500" onClick={snoozeNudge}>Later</button>
          </div>
        </div>
      )}

      {/* tour picker — CONTEXT-AWARE: with the import review (or the Manage
          drawer) open, only that screen's story shows; focus beats a menu. */}
      {open && (() => {
        const ctx = document.querySelector('[data-guide-context]')
          ?.getAttribute('data-guide-context');
        const ctxTours = { import: ['import-statement'], manage: ['manage-recurring'],
                           'item-detail': ['item-detail'], 'add-sheet': ['add-sheet'],
                           'savings-pot': ['savings-pot'],
                           map: ['spend-map', 'map-link', 'map-trip-story', 'map-replay', 'map-events'] }[ctx];
        // Overlay-scoped tours only surface when THEIR overlay is open —
        // the general picker stays about the pages.
        const overlayOnly = ['item-detail', 'add-sheet', 'savings-pot'];
        // Never hide the other guides: with a screen context, list THIS
        // screen's tours first, then keep every other guide right below.
        const tours = ctxTours
          ? [...visibleTours().filter((t) => ctxTours.includes(t.id)),
             ...visibleTours().filter((t) => !overlayOnly.includes(t.id) && !ctxTours.includes(t.id))]
          : visibleTours().filter((t) => !overlayOnly.includes(t.id));
        return (
        <div className="fixed inset-0 bg-black/70 grid place-items-center z-[85] px-6"
             onClick={() => setOpen(false)}>
          <div className="card max-w-md w-full max-h-[85vh] flex flex-col" onClick={(e) => e.stopPropagation()}>
            <h2 className="font-bold text-lg">
              {simple ? 'Help' : ctxTours ? 'Guides — this screen first' : 'How Vault works'}
            </h2>
            <p className="text-sm text-slate-400 mt-1">
              {simple
                ? 'Ask anything — type it, or tap 🎤 and say it. Vault answers from its guides.'
                : ctxTours
                ? 'This screen first. Every other guide is right below.'
                : 'Pick a story — bubbles walk you through the real screens, step by step.'}
            </p>
            <div className="mt-3">
              {/* The one list search — dictation now streams via VoiceButton
                  inside the shared SearchField (same native SpeechRecognition). */}
              <SearchField value={q} onChange={(t) => { setQ(t); setVoiceAsked(false); }}
                           onVoice={(t) => { setQ(t); setVoiceAsked(true); }} parse={false}
                           placeholder="Search or ask — tap 🎤 to ask out loud" />
              <div className="mt-2 flex items-center gap-2 text-xs text-slate-400">
                <span>Voice:</span>
                {[['device', 'Device'], ['ai', 'Natural (AI)']].map(([k, label]) => (
                  <button key={k} onClick={() => setVoice(k)}
                          className={`rounded-full border px-2.5 py-0.5 ${voice === k ? 'border-vpurple text-white' : 'border-edge'}`}>{label}</button>
                ))}
              </div>
            </div>
            {ask && (ask.loading || ask.answer || (ask.picks && ask.picks.length > 0)) && (
              <div className="mt-4 rounded-xl border border-vpurple/50 bg-vpurple/5 p-3" data-guide-ask>
                <div className="text-[11px] uppercase tracking-wide text-vpurple mb-1">✨ Answer from the guides</div>
                {ask.loading ? <p className="text-sm text-slate-400">Looking through the guides…</p> : (<>
                  {ask.answer && (
                    <p className="text-sm text-slate-200 mb-2">
                      {ask.answer}{' '}
                      <button className="text-vpurple text-xs" aria-label="Read the answer aloud"
                              onClick={() => speakText(ask.answer, voice)}>🔊 Listen</button>
                    </p>
                  )}
                  <div className="grid gap-2">
                    {!simple && (ask.picks || []).map((r) => {
                      const t = visibleTours().find((x) => x.id === r.id);
                      if (!t) return null;
                      return (
                        <button key={`ask-${r.id}`} className="text-left border border-vpurple/50 rounded-xl px-3 py-2 hover:border-vpurple transition"
                                onClick={() => start(t)}>
                          <span className="font-semibold text-sm">Show me: {t.title}</span>
                          <span className="block text-xs text-vpurple/80">{r.reason || t.blurb}</span>
                        </button>
                      );
                    })}
                  </div>
                </>)}
              </div>
            )}
            {!ctxTours && !q.trim() && recs && recs.length > 0 && (
              <div className="mt-4">
                <div className="text-[11px] uppercase tracking-wide text-vpurple mb-1">⭐ For you</div>
                <div className="grid gap-2">
                  {recs.map((r) => {
                    const t = visibleTours().find((x) => x.id === r.id);
                    if (!t) return null;
                    return (
                      <button key={`rec-${r.id}`}
                              className="text-left border border-vpurple/50 bg-vpurple/5 rounded-xl p-4 hover:border-vpurple transition"
                              onClick={() => start(t)}>
                        <span className="font-semibold">{done.has(t.id) ? "✓ " : ""}{t.title}</span>
                        <span className="block text-sm text-vpurple/80">{r.reason || t.blurb}</span>
                      </button>
                    );
                  })}
                </div>
              </div>
            )}
            {simple && (
              <button className="mt-4 text-left border border-edge rounded-xl p-4 hover:border-vblue transition"
                      onClick={() => { setOpen(false); setMode('full', { toast: 'Showing everything — your guides are in the 📖.', then: () => window.dispatchEvent(new Event('vault-open-guide')) }); }}>
                <span className="font-semibold">See every guide</span>
                <span className="block text-sm text-slate-400">Switches to Everything and opens the step-by-step walkthroughs. Switch back any time.</span>
              </button>
            )}
            {!simple && <div className="mt-4 grid gap-2 overflow-y-auto min-h-0">
              {tours.filter((t) => !q || (t.title + " " + t.blurb).toLowerCase().includes(q.toLowerCase())).map((t) => (
                <button key={t.id}
                        className="text-left border border-edge rounded-xl p-4 hover:border-vblue transition"
                        onClick={() => start(t)}>
                  <span className="font-semibold">{done.has(t.id) ? "✓ " : ""}{t.title}</span>
                  <span className="block text-sm text-slate-400">{t.blurb}</span>
                </button>
              ))}
            </div>}
            <button className="mt-4 text-sm text-slate-500" onClick={() => setOpen(false)}>Close</button>
          </div>
        </div>
        );
      })()}

      {/* active tour: dim everything, spotlight the target, bubble the story */}
      {tour && step && (
        <div className="fixed inset-0 z-[85] pointer-events-none">
          <div className="absolute inset-0 bg-black/60" style={rect ? {
            clipPath: `polygon(0 0, 100% 0, 100% 100%, 0 100%, 0 ${rect.top - 6}px,
              ${rect.left - 6}px ${rect.top - 6}px, ${rect.left - 6}px ${rect.top + rect.height + 6}px,
              ${rect.left + rect.width + 6}px ${rect.top + rect.height + 6}px,
              ${rect.left + rect.width + 6}px ${rect.top - 6}px, 0 ${rect.top - 6}px)`,
          } : {}} />
          {rect && (
            <div className="absolute rounded-xl ring-2 ring-vpurple animate-pulse"
                 style={{ top: rect.top - 6, left: rect.left - 6,
                          width: rect.width + 12, height: rect.height + 12 }} />
          )}
          <div className="absolute card !p-4 pointer-events-auto shadow-2xl border-vpurple/50 flex flex-col"
               style={bubbleStyle}>
            <div className="text-[10px] uppercase tracking-wide text-vpurple">
              {tour.title} · step {idx + 1} of {tour.steps.length}
            </div>
            <div className="flex items-start gap-2 mt-1 shrink-0">
              <h3 className="font-bold flex-1">{step.title}</h3>
              <button className="text-xs text-vpurple shrink-0" aria-label="Read this step aloud"
                      onClick={() => speakText(`${step.title}. ${step.body}`, voice)}>🔊</button>
            </div>
            <p className="text-sm text-slate-400 mt-1 overflow-y-auto min-h-0 flex-1">{step.body}</p>
            <div className="mt-3 flex items-center gap-3 shrink-0">
              {step.advanceOn === 'click'
                ? <span className="text-xs text-vpurple">Click the highlighted button to continue…</span>
                : <button className="btn-primary !py-1.5 !px-4 text-sm" onClick={next}>
                    {idx + 1 < tour.steps.length ? 'Next' : 'Done'}
                  </button>}
              <button className="text-xs text-slate-500" onClick={stop}>Exit guide</button>
              <label className="ml-auto text-xs text-slate-400 flex items-center gap-1 cursor-pointer">
                <input type="checkbox" checked={readAloud} onChange={(e) => { setReadAloud(e.target.checked); if (!e.target.checked) stopSpeaking(); }} />
                Read aloud
              </label>
            </div>
          </div>
        </div>
      )}
    </>
  );
}
